import { clsx } from "clsx";
import type { FC, ReactNode } from "react";
import { useState } from "react";
import { Icon } from "../Icons";
import "./SearchInput.css";

/**
 * Props for the SearchInput component
 */
export interface SearchInputProps {
  /**
   * Initial value of the search input
   */
  initialValue?: string;

  /**
   * Callback function when the input value changes
   */
  onChange: (value: string) => void;

  /**
   * Placeholder text to display when the input is empty
   */
  placeholder?: string;

  /**
   * Whether the input is currently loading
   */
  isLoading?: boolean;

  /**
   * Callback for when the input gains focus
   */
  onFocus?: () => void;

  /**
   * Callback for when the input loses focus
   */
  onBlur?: () => void;

  /**
   * CSS class name to apply to the container
   */
  className?: string;

  /**
   * Optional render prop for the search icon
   */
  renderIcon?: (isFocused: boolean) => ReactNode;

  /**
   * Optional render prop for the loading indicator
   */
  renderLoadingIndicator?: () => ReactNode;

  /**
   * Size variant of the search input
   */
  size?: "md" | "lg";

  /**
   * Whether the input should be collapsible (shows only icon until clicked)
   */
  collapsible?: boolean;

  /**
   * Callback for keyboard events on the input field
   */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

/**
 * SearchInput component
 *
 * A reusable search input field with loading state and focus handling
 */
export const SearchInput: FC<SearchInputProps> = ({
  initialValue,
  onChange,
  placeholder = "Search",
  isLoading = false,
  onFocus,
  onBlur,
  className = "",
  renderIcon,
  renderLoadingIndicator,
  size = "md",
  collapsible = false,
  onKeyDown,
}) => {
  const [value, setValue] = useState(initialValue);
  const [isFocused, setIsFocused] = useState(false);
  const [isExpanded, setIsExpanded] = useState(!collapsible);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    onChange(e.target.value);
  };

  const handleClear = () => {
    setValue("");
    onChange("");

    // If collapsible, collapse the input after clearing
    if (collapsible) {
      setIsExpanded(false);
      // Remove focus from the input to complete the collapse
      const inputElement = document.getElementById(
        "search-input",
      ) as HTMLInputElement;
      if (inputElement) {
        inputElement.blur();
      }
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (collapsible) {
      setIsExpanded(true);
    }
    onFocus?.();
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (collapsible && !value) {
      setIsExpanded(false);
    }
    onBlur?.();
  };

  const handleIconClick = () => {
    if (collapsible && !isExpanded) {
      setIsExpanded(true);
      // Focus the input after expansion
      setTimeout(() => {
        const inputElement = document.getElementById(
          "search-input",
        ) as HTMLInputElement;
        if (inputElement) {
          inputElement.focus();
        }
      }, 100);
    }
  };

  return (
    <div
      className={clsx(
        "indigo-search-input",
        `indigo-search-input--size-${size}`,
        {
          "indigo-search-input--collapsible": collapsible,
          "indigo-search-input--expanded": isExpanded,
        },
        className,
      )}
      onClick={handleIconClick}
    >
      {isLoading ? (
        renderLoadingIndicator ? (
          renderLoadingIndicator()
        ) : (
          <div className="indigo-search-input__loading-indicator" />
        )
      ) : renderIcon ? (
        <label
          htmlFor="search-input"
          aria-hidden="true"
          className="indigo-search-input__icon-wrapper"
        >
          {renderIcon(isFocused)}
        </label>
      ) : (
        <label
          htmlFor="search-input"
          className="indigo-search-input__icon-wrapper"
        >
          <Icon
            name="search"
            size={size === "lg" ? "s" : "xs"}
            className="indigo-search-input__icon"
            svgProps={{ "aria-hidden": true }}
          />
        </label>
      )}

      <input
        id="search-input"
        name="search"
        type="text"
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className="indigo-search-input__field"
        aria-label="Search"
        autoComplete="off"
        spellCheck="false"
      />

      {value && isExpanded && (
        <Icon
          name="close"
          size="s"
          className="indigo-search-input__clear-btn"
          svgProps={{
            "aria-label": "Clear search",
            onClick: handleClear,
            role: "button",
            tabIndex: 0,
            onKeyDown: (e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleClear();
              }
            },
          }}
        />
      )}
    </div>
  );
};

export default SearchInput;
